/**
 * Utilidades de rondas para el cuadro: nombres de fase, estadísticas por ronda
 * y cálculo de las columnas de cabecera (alineadas con la geometría del SVG).
 */
import { winningPathLength } from './BracketTree';

const ROUND_SHORT = ['', 'Final', 'Semis', 'Cuartos', 'Octavos', '16avos', '32avos', '64avos', '128avos'];

/** Nombre corto de la fase a partir de su profundidad (1 = Final). */
export function roundShortName(depthFromFinal) {
  const d = Number(depthFromFinal);
  if (Number.isFinite(d) && d >= 1 && d < ROUND_SHORT.length) return ROUND_SHORT[d];
  if (Number.isFinite(d) && d >= 1) return `${Math.pow(2, d - 1)}avos`;
  return '';
}

/**
 * Estadísticas por ronda del subárbol: Map(depth -> { total, completed }).
 * depth 1 = el propio game (la final del subárbol).
 */
export function subtreeRoundStats(game) {
  const map = new Map();
  const visited = new Set();
  const walk = (g, depth) => {
    if (!g || !g.id || visited.has(g.id)) return;
    visited.add(g.id);
    const entry = map.get(depth) || { total: 0, completed: 0 };
    entry.total += 1;
    if (g.completed) entry.completed += 1;
    map.set(depth, entry);
    const sides = g.sides ? Object.values(g.sides) : [];
    for (const side of sides) {
      const seed = side && side.seed;
      if (seed && seed.sourceGame && Number(seed.rank) === 1) {
        walk(seed.sourceGame, depth + 1);
      }
    }
  };
  walk(game, 1);
  return map;
}

/** Recorre el árbol y devuelve todos los partidos (sin duplicados). */
export function collectAllGames(game) {
  const out = [];
  const visited = new Set();
  const walk = (g) => {
    if (!g || !g.id || visited.has(g.id)) return;
    visited.add(g.id);
    out.push(g);
    const sides = g.sides ? Object.values(g.sides) : [];
    for (const side of sides) {
      const sg = side && side.seed && side.seed.sourceGame;
      if (sg) walk(sg);
    }
  };
  walk(game);
  return out;
}

/** ¿Debe pintarse este cuadro a dos lados? (más de 16 equipos = 5+ rondas). */
export function shouldRenderTwoSided(game) {
  if (winningPathLength(game) < 5) return false;
  return Boolean(game?.sides?.home?.seed?.sourceGame && game?.sides?.visitor?.seed?.sourceGame);
}

/**
 * Columnas de cabecera para un cuadro a una sola columna (final a la derecha).
 * La geometría replica la de BracketTree.
 */
export function buildOneSidedColumns(game, gameW, roundGap, svgPadding) {
  const numRounds = winningPathLength(game);
  const stats = subtreeRoundStats(game);
  const svgWidth = numRounds * (gameW + roundGap) + svgPadding * 2;
  const columns = [];
  for (let d = numRounds; d >= 1; d--) {
    const cardX = svgWidth - svgPadding - gameW - (d - 1) * (gameW + roundGap);
    const s = stats.get(d) || { total: 0, completed: 0 };
    columns.push({
      cx: cardX + gameW / 2,
      index: numRounds - d + 1,
      name: roundShortName(d),
      completed: s.completed,
      total: s.total,
    });
  }
  return { columns, svgWidth, numRounds };
}

/**
 * Columnas de cabecera para un cuadro a dos lados (final en el centro).
 * La geometría replica la de TwoSidedBracketTree.
 */
export function buildTwoSidedColumns(game, gameW, roundGap, svgPadding) {
  const homeChild = game?.sides?.home?.seed?.sourceGame;
  const visitorChild = game?.sides?.visitor?.seed?.sourceGame;
  const leftRounds = winningPathLength(homeChild);
  const rightRounds = winningPathLength(visitorChild);
  const fullStats = subtreeRoundStats(game);
  const totalCols = leftRounds + 1 + rightRounds;
  const svgWidth = totalCols * gameW + (totalCols - 1) * roundGap + svgPadding * 2;
  const step = gameW + roundGap;
  const columns = [];

  for (let c = 0; c < leftRounds; c++) {
    const x = svgPadding + c * step;
    const fullDepth = leftRounds - c + 1;
    const s = fullStats.get(fullDepth) || { total: 0, completed: 0 };
    columns.push({
      cx: x + gameW / 2,
      index: c + 1,
      name: roundShortName(fullDepth),
      completed: s.completed,
      total: s.total,
    });
  }

  const finalX = svgPadding + leftRounds * step;
  const finalStats = fullStats.get(1) || { total: 1, completed: game?.completed ? 1 : 0 };
  columns.push({
    cx: finalX + gameW / 2,
    index: leftRounds + 1,
    name: roundShortName(1),
    completed: finalStats.completed,
    total: finalStats.total,
  });

  for (let c = 0; c < rightRounds; c++) {
    const x = finalX + (c + 1) * step;
    const fullDepth = c + 2;
    const s = fullStats.get(fullDepth) || { total: 0, completed: 0 };
    columns.push({
      cx: x + gameW / 2,
      index: rightRounds - c,
      name: roundShortName(fullDepth),
      completed: s.completed,
      total: s.total,
    });
  }

  return { columns, svgWidth, leftRounds, rightRounds };
}

/**
 * Geometría de la tarjeta de 3º/4º puesto: se coloca debajo de la final,
 * en la misma columna (mismo x que la final).
 */
export function thirdPlaceGeometry(gameH, finalYAbs, svgPadding) {
  const gap = Math.round(gameH * 0.5);
  const y = finalYAbs + gameH + gap;
  return { y, neededHeight: y + gameH + svgPadding };
}

/**
 * Tamaño natural (px) del SVG del cuadro, replicando la geometría de
 * BracketTree / TwoSidedBracketTree. Útil para dimensionarlo en móvil.
 */
export function bracketSvgSize(
  game,
  twoSided,
  gameW,
  gameH,
  roundGap,
  svgPadding,
  bandHeight,
  hasThirdPlace = false
) {
  let width;
  let verticalRounds;
  if (twoSided) {
    const homeChild = game?.sides?.home?.seed?.sourceGame;
    const visitorChild = game?.sides?.visitor?.seed?.sourceGame;
    const leftRounds = winningPathLength(homeChild);
    const rightRounds = winningPathLength(visitorChild);
    verticalRounds = Math.max(leftRounds, rightRounds, 1);
    const totalCols = leftRounds + 1 + rightRounds;
    width = totalCols * gameW + (totalCols - 1) * roundGap + svgPadding * 2;
  } else {
    const numRounds = Math.max(winningPathLength(game), 1);
    verticalRounds = numRounds;
    width = numRounds * (gameW + roundGap) + svgPadding * 2;
  }
  const innerHeight = gameH * Math.pow(2, verticalRounds - 1) + svgPadding * 2;
  let height = innerHeight + bandHeight;
  if (hasThirdPlace) {
    const finalYAbs = bandHeight + innerHeight / 2 - gameH / 2;
    height = Math.max(height, thirdPlaceGeometry(gameH, finalYAbs, svgPadding).neededHeight);
  }
  return { width, height };
}
